/**
 * Authors: mariushirjanu
 * Created: 21/01/2025
 */
import _ from 'underscore';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { GRID_FAQ_DOCUMENTS } from '@app/shared/app-grids';
import { ColSpanParams, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { GaAgGridService } from '@app/shared/ag-grid-components/ga-ag-grid.service';
import { IColDef } from '@app/shared/report/report-grid.service';
import { CellRendererComp } from '@app/shared/ag-grid-components/ga-ag-grid.component';
import { DocumentDataService, DOCUMENTS_URL, DocumentTableReference } from '@app/shared/document/document-data.service';
import { IDocument, IFaq } from '@type_definitions/datatypes';
import { FormattingService } from '@app/shared/services/formatting.service';
import { FileUploadService } from '@app/shared/file-upload/file-upload.service';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '@app/shared/ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { IAgButtonRendererParams } from '@app/shared/ag-grid-components/ag-button/ag-button-renderer.component';
import { util } from '@app/shared/util';

enum GridColumn {
  OPTIONS = 'options',
  TYPE = 'type',
  FILENAME = 'fileName',
  DESCRIPTION = 'description',
  DATE_ADDED = 'createdDate',
  ADDED_BY = 'userId'
}

@Component({
  selector: 'faq-manage-documents',
  template: `
    <ga-ag-grid [hidden]="!(hasDocuments && model?.id)" [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit" height="200" class="mb-2"></ga-ag-grid>
    <p *ngIf="!loading && !hasDocuments && model?.id" class="pt-1">No documents found</p>
    <p *ngIf="!model?.id" class="pt-1">The new FAQ has to be saved before being able to add documents</p>
    <button *ngIf="model?.id" class="btn btn-primary" (click)="openUploadDialog()">
      <i class="fa fa-folder-open-o"></i> Add document
    </button>
  `
})
export class FaqManageDocumentsComponent implements OnInit, OnChanges {
  @Input() public model:IFaq;

  public gridOptions:GridOptions;
  public gridInit:boolean;
  public loading:boolean;
  public hasDocuments:boolean;
  public saving:boolean;
  public readonly gridId:string = GRID_FAQ_DOCUMENTS;

  constructor(
    private gaAgGridService:GaAgGridService,
    private documentDataService:DocumentDataService,
    private formattingService:FormattingService,
    private fileUploadService:FileUploadService
  ) {}

  public ngOnInit():void {
    this.gridInitialize();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes?.model) {
      this.hasDocuments = changes.model.currentValue?.attachments?.length > 0;
    }
  }

  private getColDefs():IColDef[] {
    const colDefs:IColDef[] = [
      {
        field: GridColumn.OPTIONS,
        headerName: '',
        pinned: 'left',
        width: 46,
        cellClass: 'cell-actions text-center',
        cellRenderer: CellRendererComp.BUTTON_RENDERER,
        cellRendererParams: {
          icon: 'fa-pencil',
          onClick: (params:IAgButtonRendererParams) => this.onEditDocument(params),
          className: 'btn-sm'
        },
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true
      },
      {
        field: GridColumn.OPTIONS,
        headerName: '',
        sortable: false,
        resizable: false,
        pinned: 'left',
        width: 46,
        suppressHeaderMenuButton: true,
        cellClass: 'cell-actions text-center',
        cellRenderer: CellRendererComp.BUTTON_RENDERER,
        cellRendererParams: {
          icon: 'fa-trash-o',
          onClick: this.onDeleteDocument,
          className: 'btn-sm'
        },
        suppressSizeToFit: true,
        suppressAutoSize: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true
      },
      {
        field: GridColumn.FILENAME,
        width: 100,
        headerName: 'Filename',
        cellRenderer: CellRendererComp.RICH_TEXT_RENDERER,
        cellRendererParams: <IRichTextRendererParams>{
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            return [{
              label: params.value,
              href: `/unacc/AttachWriter?row0_Deattid=${params.data.id}`
            }];
          },
          type: RichTextType.LINK
        },
        colSpan: (params:ColSpanParams):number => params.node.isRowPinned()? 2 : 1
      },
      {
        field: GridColumn.TYPE,
        headerName: 'Type',
        valueGetter: 'data.documentType && data.documentType.description'
      },
      {
        field: GridColumn.DATE_ADDED,
        headerName: 'Date Added',
        valueFormatter: (params:ValueFormatterParams):string => this.formattingService.formatDate(params.value)
      },
      {
        field: GridColumn.DESCRIPTION,
        headerName: 'Description'
      }
    ];

    return colDefs;
  }

  private gridInitialize():void {
    this.gridOptions = {
      columnDefs: this.getColDefs(),
      onGridReady: ():void => {
        if(this.hasDocuments) {
          this.loadDocuments();
        } else {
          this.gaAgGridService.setRowData(this.gridId, []);
        }
      },
      onFirstDataRendered: ():void => {
        // autosize all columns except the OPTIONS
        const columDef:IColDef[] = _.reject(this.getColDefs(), (column:IColDef) => column.field === GridColumn.OPTIONS);
        this.gaAgGridService.autoSizeColumns(this.gridId, _.pluck(columDef, 'field'));
      },
      suppressMovableColumns: true
    };

    this.gridInit = true;
  }

  private loadDocuments():void {
    this.loading = true;
    this.documentDataService.getDocuments(DocumentTableReference.FAQ, this.model.id)
      .then((documents:IDocument[]) => {
        this.gaAgGridService.setRowData(this.gridId, documents);
        this.hasDocuments = documents?.length > 0;
      })
      .catch(() => {
        this.gaAgGridService.setRowData(this.gridId, []);
        this.hasDocuments = false;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private onEditDocument = (params:IAgButtonRendererParams):void => {
    this.openUploadDialog(params);
  };

  private onDeleteDocument = (params:IAgButtonRendererParams):void => {
    params.busy = true;
    this.fileUploadService.showDeleteFileConfirmationDialog(params.data)
      .then(() => {
        this.loadDocuments();
      })
      .catch(util.noop)
      .finally(() => {
        params.busy = false;
      });
  };

  public openUploadDialog(params?:IAgButtonRendererParams): void {
    params = params || <any>{};
    const document:IDocument = params.data;
    params.busy = true;
    this.fileUploadService.showFileUploadDialog(DOCUMENTS_URL, {
      document,
      tableReference: DocumentTableReference.FAQ,
      recordId: this.model?.id,
      editableDocumentType: true
    })
      .then(() => {
        this.loadDocuments();
      })
      .finally(() => {
        params.busy = false;
      });
  }
}
